<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedMenu: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const iconInput = ref('i-lucide-link');
const urlInput = ref('');
const enabled = ref(true);
const position = ref(0);

const nameInputRef = ref(null);

const addCustomMenu = reactive({
  showLoading: false,
  message: '',
});

// Common Lucide icons for selection
const availableIcons = [
  'i-lucide-link',
  'i-lucide-chart-bar',
  'i-lucide-chart-line',
  'i-lucide-chart-pie',
  'i-lucide-gauge',
  'i-lucide-activity',
  'i-lucide-database',
  'i-lucide-layout-dashboard',
  'i-lucide-file-text',
  'i-lucide-folder',
  'i-lucide-globe',
  'i-lucide-external-link',
  'i-lucide-bar-chart',
  'i-lucide-trending-up',
  'i-lucide-pie-chart',
  'i-lucide-calendar',
  'i-lucide-clock',
  'i-lucide-settings',
  'i-lucide-tool',
  'i-lucide-zap',
];

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  urlInput: { required, url },
}));

const v$ = useVuelidate(rules, { name, urlInput });

const resetForm = () => {
  name.value = '';
  iconInput.value = 'i-lucide-link';
  urlInput.value = '';
  enabled.value = true;
  position.value = 0;
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedMenu.name || '';
  iconInput.value = props.selectedMenu.icon || 'i-lucide-link';
  urlInput.value = props.selectedMenu.url || '';
  enabled.value = props.selectedMenu.enabled ?? true;
  position.value = props.selectedMenu.position || 0;
};

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  nameInputRef.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_MENU.EDIT.${base}`
    : `CUSTOM_MENU.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleCustomMenu = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomMenu.showLoading = true;
  try {
    const menuData = {
      name: name.value,
      icon: iconInput.value,
      url: urlInput.value,
      enabled: enabled.value,
      position: position.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customMenu/updateCustomMenu', {
        id: props.selectedMenu.id,
        ...menuData,
      });
      useAlert(t('CUSTOM_MENU.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customMenu/createCustomMenu', menuData);
      useAlert(t('CUSTOM_MENU.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t('CUSTOM_MENU.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    addCustomMenu.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomMenu.showLoading
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomMenu">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_MENU.FORM.NAME.LABEL') }}
          <input
            ref="nameInputRef"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_MENU.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.urlInput.$error }">
          {{ $t('CUSTOM_MENU.FORM.URL.LABEL') }}
          <input
            v-model.trim="urlInput"
            type="text"
            :placeholder="$t('CUSTOM_MENU.FORM.URL.PLACEHOLDER')"
            @blur="v$.urlInput.$touch"
          />
        </label>
        <p class="text-xs text-n-slate-10 mb-4">
          {{ $t('CUSTOM_MENU.FORM.URL.HELP_TEXT') }}
        </p>
      </div>

      <div class="w-full">
        <label>
          {{ $t('CUSTOM_MENU.FORM.ICON.LABEL') }}
        </label>
        <div class="flex flex-wrap gap-2 mb-4">
          <button
            v-for="icon in availableIcons"
            :key="icon"
            type="button"
            class="p-2 rounded-lg border transition-all"
            :class="
              iconInput === icon
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="iconInput = icon"
          >
            <span :class="icon" class="size-5 text-n-slate-12" />
          </button>
        </div>
      </div>

      <div class="w-full">
        <label>
          {{ $t('CUSTOM_MENU.FORM.POSITION.LABEL') }}
          <input
            v-model.number="position"
            type="number"
            min="0"
            :placeholder="$t('CUSTOM_MENU.FORM.POSITION.PLACEHOLDER')"
          />
        </label>
        <p class="text-xs text-n-slate-10 mb-4">
          {{ $t('CUSTOM_MENU.FORM.POSITION.HELP_TEXT') }}
        </p>
      </div>

      <div class="w-full flex items-center gap-2 mb-4">
        <input
          id="menu-enabled"
          v-model="enabled"
          type="checkbox"
          class="ltr:mr-2 rtl:ml-2"
        />
        <label
          for="menu-enabled"
          class="text-sm font-normal mb-0 cursor-pointer"
        >
          {{ $t('CUSTOM_MENU.FORM.ENABLED.LABEL') }}
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_MENU.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomMenu.showLoading"
        />
      </div>
    </form>
  </div>
</template>
